import { escapar } from "./escapar";

/**
 * A identificação de quem reporta, no formulário de report (DEV-103).
 *
 * Puro de propósito: a regra abaixo é uma decisão de produto com um branch, e branch de
 * produto merece teste. O resto do formulário é montado dentro do `WidgetUI`, onde só um
 * screenshot alcança — e screenshot não trava regra nenhuma.
 */

/**
 * "Parece um e-mail?" — a MESMA pergunta em dois lugares, de propósito: decide se o e-mail do
 * host serve (e então não há input) e valida o que a pessoa digita antes de enviar. Duas
 * cópias divergindo daria o pior caso: o host passa a checagem de um lado e é recusado do
 * outro, com o erro desenhado num campo que não existe.
 *
 * Deliberadamente frouxa. Quem decide de verdade é o intake (regra `email` do Form Request);
 * isto existe só para ninguém escrever o relato inteiro e descobrir o problema num 422 no fim.
 */
export function emailParece(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

/**
 * O e-mail que a página HOST afirmou no init — e só se for utilizável.
 *
 * Host que manda algo malformado cai no mesmo caminho de quem não mandou nada: o input volta,
 * já preenchido com o valor dele, e a pessoa conserta. Sem isso o formulário travava em
 * SILÊNCIO nesse caso — a validação de formato desenha o erro em `[data-erro="email"]` e dá
 * `focus()` no input, e nenhum dos dois existe na versão sem campo.
 */
export function emailDoHostUtilizavel(reporterEmail: string | undefined): string {
  const bruto = reporterEmail?.trim() ?? "";

  return emailParece(bruto) ? bruto : "";
}

export interface CampoEmailArgs {
  /** `cfg.reporter?.email` — cru, como o host mandou. */
  reporterEmail: string | undefined;
  /** O que a pessoa digitou na última vez neste browser (localStorage). */
  emailLembrado: string;
  /** O relator recusa report anônimo? Então o campo é obrigatório. */
  obrigatorio: boolean;
  /**
   * Quem entrou, quando o relator EXIGE login (DEV-260): e-mail (ou nome, se o provedor
   * não devolveu e-mail). Presente = não há campo nenhum, só esta linha — a identidade do
   * chamado é a da sessão, e o servidor a carimba sem consultar o formulário.
   *
   * String vazia é um caso real (sessão de bundle anterior, guardada antes de o e-mail
   * viajar no `postMessage`): a linha aparece sem o endereço, dizendo que a pessoa está
   * identificada, que é a parte que importa.
   */
  identidade?: string;
}

/**
 * **Com e-mail do host, o bloco NÃO existe** — nem input, nem o endereço em texto. O host
 * acabou de declarar quem está ali (ele conhece a sessão dele, e é dessa mesma fonte que sai o
 * `reporter` do payload); oferecer um campo editável em cima disso convida a divergir da
 * identidade que ele afirmou, e mostrá-lo só para leitura gastava uma linha do formulário
 * repetindo à pessoa o e-mail dela — informação que ela já tem, num lugar onde nada há para
 * decidir. O envio continua levando o endereço: `widget.ts` cai no `emailDoHost()` quando o
 * input não existe.
 *
 * **Com login obrigatório (DEV-260) não há campo, e sim uma LINHA** dizendo em nome de quem
 * o chamado vai. Ver `identidade`, acima.
 *
 * **Sem host, o input existe** e é a única identificação que o chamado vai ter. Ele aparece
 * mesmo quando o relator aceita anônimo (só deixa de ser obrigatório): antes o widget não tinha
 * campo nenhum, então em host que não cooperava o chamado nascia órfão — e nem o portal nem o
 * "Meus chamados" de outro aparelho o encontravam, porque os dois casam por
 * `bug_reports.reporter_email`.
 */
export function htmlDoCampoEmail({ reporterEmail, emailLembrado, obrigatorio, identidade }: CampoEmailArgs): string {
  // Com login obrigatório não há o que digitar nem o que decidir: o chamado sai no nome da
  // conta que entrou. A linha existe (ao contrário do caso do host, onde o bloco some) por
  // ser o RECIBO do login — a pessoa acabou de passar por uma janela de outro domínio, e
  // sem isto o formulário voltaria idêntico ao de quem não entrou.
  if (identidade !== undefined) {
    return (
      `<p class="ident-linha">` +
      (identidade !== ""
        ? `Chamado aberto como <strong>${escapar(identidade)}</strong>`
        : "Chamado aberto com a sua conta") +
      `</p>`
    );
  }
  if (emailDoHostUtilizavel(reporterEmail) !== "") return "";

  // O valor bruto do host (malformado) tem precedência sobre a memória do browser: ele é a
  // pista do que a pessoa precisa corrigir.
  const inicial = reporterEmail?.trim() || emailLembrado;

  return (
    `<div class="field">` +
    `<label class="label" for="altbug-email">E-mail${obrigatorio ? "" : " (opcional)"}</label>` +
    `<input class="input" id="altbug-email" type="email" inputmode="email" autocomplete="email"` +
    ` placeholder="voce@exemplo.com" value="${escapar(inicial)}"` +
    `${obrigatorio ? ' required aria-required="true"' : ""} />` +
    `<p class="hint campo-hint">${
      obrigatorio
        ? "Precisamos dele para responder e para você acompanhar o chamado."
        : "Sem e-mail o chamado é aberto, mas você não consegue acompanhá-lo depois."
    }</p>` +
    `<p class="erro-campo" data-erro="email" hidden></p>` +
    `</div>`
  );
}
